// meeting-note-citations — resolve a call summary's markdown citations.
//
// ATLAS writes KB-note / aiSummary bodies as markdown that grounds each claim
// with an inline `[^N]` marker, then closes the body with a footnote-definition
// block whose definitions point at transcript line ids:
//
//     ...education funding[^4].
//
//     [^1]: bf1836a1-l2
//     [^4]: bf1836a1-l12, bf1836a1-l13
//
// `prepareSummary` lifts that block out of the prose and hands the renderer two
// things: markdown in which every kept marker has become a `#citation-N` link,
// and the resolved citations themselves (speaker + timestamp + verbatim text of
// each cited transcript line). `<CitedSummary>` swaps the link carrier for an
// interactive `[N]` marker — hover for the quote, click to jump to the cited
// transcript line — so nothing needs a separate "Citations" list at the foot of
// the summary, and the renderer never parses markdown itself.
//
// Markers are renumbered 1..n in order of first appearance: ATLAS's own numbers
// can be sparse or out of order, and the reader never sees the raw body.
// Orphaned markers (referenced in prose, no definition — older rows predate the
// mandatory-block prompt, atlas-v3) are stripped and reported.
//
// Pure and deterministic — safe in render paths.

export interface CitationSource {
    /** Transcript line id, e.g. `bf1836a1-l2`. */
    id: string;
    speaker: string;
    /** Display offset within the call, e.g. `0:13`. */
    timestamp: string;
    text: string;
}

export interface PreparedCitation {
    /** 1-based display number, in order of first appearance in the prose. */
    number: number;
    /** ATLAS's own marker, as written in the body (`[^4]` → `4`). */
    marker: string;
    /** Transcript line ids the definition points at, in definition order. */
    lineIds: string[];
    /** The cited lines that exist in this call's transcript (may be empty). */
    sources: CitationSource[];
}

export interface PreparedSummary {
    /** Markdown ready for react-markdown; markers are `#citation-N` links. */
    markdown: string;
    /** Citations kept in the prose, ordered by (and indexed from) number 1. */
    citations: PreparedCitation[];
    /** Markers referenced in prose whose definition was missing (dropped from output). */
    orphanedMarkers: string[];
}

/** Href prefix of the link carrier `<CitedSummary>` swaps for a `[N]` marker. */
export const CITATION_HREF_PREFIX = '#citation-';

// A footnote definition line: `[^N]: <value>` at the start of a (trimmed) line.
const DEF_LINE_RE = /^\[\^([^\]\s]+)\]:[ \t]*(.*)$/;
// An inline footnote reference anywhere in prose.
const REF_RE = /\[\^([^\]\s]+)\]/g;

/** The markdown a kept marker becomes: link text `[N]`, href the carrier. */
function citationLink(number: number): string {
    return `[\\[${number}\\]](${CITATION_HREF_PREFIX}${number})`;
}

/** Parse a citation number back out of a carrier href; null when it isn't one. */
export function citationNumberFromHref(href: string): number | null {
    if (!href.startsWith(CITATION_HREF_PREFIX)) return null;
    const raw = href.slice(CITATION_HREF_PREFIX.length);
    if (!/^\d+$/.test(raw)) return null;
    return Number(raw);
}

export function prepareSummary(
    body: string,
    sources: readonly CitationSource[] = [],
): PreparedSummary {
    const sourceById = new Map<string, CitationSource>();
    for (const s of sources) sourceById.set(s.id, s);

    // 1. Pull the definition block out of the body, line by line.
    const defs = new Map<string, string>();
    const proseLines: string[] = [];
    for (const line of body.split('\n')) {
        const m = line.trimStart().match(DEF_LINE_RE);
        if (m) {
            defs.set(m[1], m[2]);
        } else {
            proseLines.push(line);
        }
    }

    // 2. Walk the prose references: number those with a definition (first
    //    appearance wins), strip orphans.
    const citations: PreparedCitation[] = [];
    const numberByMarker = new Map<string, number>();
    const orphanedMarkers: string[] = [];
    let prose = proseLines.join('\n').replace(REF_RE, (_raw, marker: string) => {
        const seen = numberByMarker.get(marker);
        if (seen !== undefined) return citationLink(seen);

        const value = defs.get(marker);
        if (value === undefined) {
            if (!orphanedMarkers.includes(marker)) orphanedMarkers.push(marker);
            return '';
        }

        const number = citations.length + 1;
        numberByMarker.set(marker, number);
        const lineIds = value
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        const resolved: CitationSource[] = [];
        for (const id of lineIds) {
            const src = sourceById.get(id);
            if (src) resolved.push(src);
        }
        citations.push({ number, marker, lineIds, sources: resolved });
        return citationLink(number);
    });

    // A `---` rule used only to separate the (now removed) definition block from
    // the prose is left dangling — trim a trailing rule + whitespace.
    prose = prose.replace(/\n+[ \t]*-{3,}[ \t]*\s*$/, '').trimEnd();

    return { markdown: prose, citations, orphanedMarkers };
}
